// src/context/AuthContext.tsx

import { createContext, useContext, useState, ReactNode, useEffect } from "react";
import {getCurrentUser, UserProfile, logout as apiLogout } from "../services/auth"; // adjust to your actual path

interface AuthContextProps {
  isAuthenticated: boolean;
  isLoading: boolean;
  handleLogin: () => Promise<void>;
  handleLogout: () => Promise<void>;
  user: UserProfile | null;
  // token: string | null;
}

// Create the context
const AuthContext = createContext<AuthContextProps | undefined>(undefined);

// Provider component
export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // On initial load, check for existing token
  useEffect(() => {
    (async () => {
      try {
        const me = await getCurrentUser();   // uses credentials: 'include'
        setUser(me);
        setIsAuthenticated(true);
      } catch {
        setUser(null);
        setIsAuthenticated(false);
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);
  

  // Call this AFTER your login() API succeeds (cookies are set)
  const handleLogin = async () => {
    try {
      const me = await getCurrentUser();
      setUser(me);
      setIsAuthenticated(true);
    } catch {
      setUser(null);
      setIsAuthenticated(false);
      // if this throws after login(), your login probably didn’t set cookies — check CORS/credentials
    }
  };

  const handleLogout = async () => {
    try {
      await apiLogout();  // calls /api/auth/logout-cookie and clears cookies server-side
    } finally {
      setUser(null);
      setIsAuthenticated(false);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        isLoading,
        handleLogin,
        handleLogout,
        user,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// ✅ The hook you import in your components!
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
